import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { AssetDialogComponent } from './asset-dialog.component';
import { DepreciationDialogComponent } from './depreciation-dialog.component';
import { environment } from '../../environments/environment';

export interface Asset {
  assetId: number;
  assetTag: string;
  assetName: string;
  categoryName: string;
  serialNumber: string;
  model: string;
  manufacturer: string;
  purchaseDate: string;
  purchaseCost: number;
  currentValue: number;
  salvageValue: number;
  usefulLifeYears: number;
  depreciationRate: number;
  accumulatedDepreciation: number;
  netBookValue: number;
  status: string;
  location: string;
  areaName: string;
  assignedEmployeeName: string;
  warrantyExpiry: string;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

export interface AssetCategory {
  categoryId: number;
  categoryName: string;
  categoryCode: string;
  description: string;
  depreciationMethod: string;
  usefulLifeYears: number;
  salvageValuePercentage: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

@Component({
  selector: 'app-asset-management',
  templateUrl: './asset-management.component.html',
  styleUrls: ['./asset-management.component.scss']
})
export class AssetManagementComponent implements OnInit {
  displayedColumns: string[] = [
    'asset_tag', 'asset_name', 'category_name', 'purchase_date', 
    'purchase_cost', 'current_value', 'net_book_value', 'status', 
    'location', 'actions'
  ];
  
  dataSource = new MatTableDataSource<Asset>();
  assets: Asset[] = [];
  categories: AssetCategory[] = [];
  loading = false;
  totalAssets = 0;
  totalValue = 0;
  totalDepreciated = 0;

  // Filters
  filterStatus = '';
  filterCategory = '';
  searchQuery = '';

  constructor(
    private http: HttpClient,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loadAssets();
    this.loadCategories();
  }

  loadAssets(): void {
    this.loading = true;
    this.http.get<Asset[]>(`${environment.apiServiceBaseUrl}/hawdco/assets`).subscribe({
      next: (data) => {
        this.assets = data;
        this.dataSource.data = data;
        this.calculateTotals();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading assets:', error);
        this.loading = false;
      }
    });
  }

  loadCategories(): void {
    this.http.get<AssetCategory[]>(`${environment.apiServiceBaseUrl}/hawdco/assets/categories`).subscribe({
      next: (data) => {
        this.categories = data;
      },
      error: (error) => {
        console.error('Error loading categories:', error);
      }
    });
  }

  calculateTotals(): void {
    this.totalAssets = this.assets.length;
    this.totalValue = this.assets.reduce((sum, asset) => sum + asset.purchaseCost, 0);
    this.totalDepreciated = this.assets.reduce((sum, asset) => sum + asset.accumulatedDepreciation, 0);
  }

  applyFilter(): void {
    let filteredAssets = this.assets;

    if (this.filterStatus) {
      filteredAssets = filteredAssets.filter(asset => asset.status === this.filterStatus);
    }

    if (this.filterCategory) {
      filteredAssets = filteredAssets.filter(asset => asset.categoryName === this.filterCategory);
    }

    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      filteredAssets = filteredAssets.filter(asset => 
        asset.assetName.toLowerCase().includes(query) ||
        asset.assetTag.toLowerCase().includes(query) ||
        asset.serialNumber.toLowerCase().includes(query)
      );
    }

    this.dataSource.data = filteredAssets;
  }

  openAssetDialog(asset?: Asset): void {
    const dialogRef = this.dialog.open(AssetDialogComponent, {
      width: '800px',
      data: asset || {}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (asset) {
          this.updateAsset(result);
        } else {
          this.createAsset(result);
        }
      }
    });
  }

  createAsset(asset: any): void {
    this.http.post(`${environment.apiServiceBaseUrl}/hawdco/assets`, asset).subscribe({
      next: () => {
        this.loadAssets();
      },
      error: (error) => {
        console.error('Error creating asset:', error);
      }
    });
  }

  updateAsset(asset: any): void {
    this.http.put(`${environment.apiServiceBaseUrl}/hawdco/assets/${asset.assetId}`, asset).subscribe({
      next: () => {
        this.loadAssets();
      },
      error: (error) => {
        console.error('Error updating asset:', error);
      }
    });
  }

  deleteAsset(assetId: number): void {
    if (confirm('Are you sure you want to delete this asset?')) {
      this.http.delete(`${environment.apiServiceBaseUrl}/hawdco/assets/${assetId}`).subscribe({
        next: () => {
          this.loadAssets();
        },
        error: (error) => {
          console.error('Error deleting asset:', error);
        }
      });
    }
  }

  viewDepreciation(asset: Asset): void {
    const dialogRef = this.dialog.open(DepreciationDialogComponent, {
      width: '900px',
      data: asset
    });
  }

  calculateDepreciation(assetId: number): void {
    this.http.post(`/api/v1/hawdco/assets/${assetId}/calculate-depreciation`, {}).subscribe({
      next: () => {
        this.loadAssets();
      },
      error: (error) => {
        console.error('Error calculating depreciation:', error);
      }
    });
  }

  exportToExcel(): void {
    this.http.get('/api/v1/hawdco/assets/export', { responseType: 'blob' }).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'assets.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('Error exporting assets:', error);
      }
    });
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'active': return 'green';
      case 'inactive': return 'orange';
      case 'disposed': return 'red';
      case 'maintenance': return 'blue';
      default: return 'gray';
    }
  }
}
